import { useState, useEffect, memo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import { VisuallyHiddenInput } from '../../uploadFile/VisuallyHiddenFileInput'

const ImageUploader = ({
	label,
	value = null,
	onChange,
	accept = 'image/*',
	multiple = false,
	width = '120px',
	height = '120px',
	showLabel = true,
	...props
}) => {
	const [preview, setPreview] = useState(null)
	const [previews, setPreviews] = useState([])

	useEffect(() => {
		if (multiple && Array.isArray(value)) {
			const urls = value.map((item) => {
				if (typeof item === 'string') return item
				if (item instanceof File) return URL.createObjectURL(item)
				return null
			}).filter(Boolean)
			setPreviews(urls)
		} else if (!multiple && value) {
			if (typeof value === 'string') {
				setPreview(value)
			} else if (value instanceof File) {
				const objectUrl = URL.createObjectURL(value)
				setPreview(objectUrl)
				return () => URL.revokeObjectURL(objectUrl)
			}
		} else {
			setPreview(null)
			setPreviews([])
		}
	}, [value, multiple])

	const handleChange = (e) => {
		const files = Array.from(e.target.files || [])
		if (!files.length) return

		if (multiple) {
			if (onChange) {
				onChange(files)
			}
		} else {
			if (onChange) {
				onChange(files[0])
			}
		}
		e.target.value = ''
	}

	const handleRemove = (index = null) => {
		if (multiple) {
			const updated = Array.isArray(value) ? [...value] : []
			updated.splice(index, 1)
			if (onChange) {
				onChange(updated)
			}
		} else {
			setPreview(null)
			if (onChange) {
				onChange(null)
			}
		}
	}

	if (multiple) {
		return (
			<Box>
				{showLabel && label && (
					<Typography 
						variant="body2" 
						sx={{
							mb: 1,
							fontSize: '14px',
							fontWeight: '500',
							color: '#313131',
						}}
						{...props.labelStyle}
					>
						{label}
					</Typography>
				)}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
						gap: '16px',
						width: '100%',
					}}
				>
					<Button
						sx={{
							width: width,
							height: height,
							borderRadius: '12px',
							aspectRatio: '1 / 1',
							backgroundColor: 'white',
							textTransform: 'none',
							padding: '0',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							border: '1px dashed #E0E0E0',
							'&:hover': {
								backgroundColor: '#F5F5F5',
								borderColor: '#7CA6BD',
							},
							'@media (max-width: 499px)': {
								margin: '0 auto',
							},
						}}
						component="label"
						variant="outlined"
					>
						<img
							src="images/DragDrop.svg"
							alt=""
							style={{ width: '28px', height: '28px' }}
						/>
						<Typography
							variant="body1"
							sx={{
								color: '#7CA6BD',
								textAlign: 'center',
								marginTop: '8px',
								fontSize: '12px',
							}}
						>
							Click to upload
						</Typography>
						<VisuallyHiddenInput
							type="file"
							accept={accept}
							onChange={handleChange}
							multiple
							{...props}
						/>
					</Button>

					{previews.map((img, idx) => (
						<Card
							key={idx}
							variant="outlined"
							sx={{
								width: width,
								height: height,
								borderRadius: '12px',
								aspectRatio: '1 / 1',
								borderColor: '#DFE4EB',
								position: 'relative',
								overflow: 'unset',
								'@media (max-width: 499px)': {
									margin: '0 auto',
								},
							}}
						>
							<CardMedia
								sx={{
									height: '100%',
									borderRadius: '12px',
								}}
								image={img}
							/>
							<button
								style={{
									position: 'absolute',
									top: '-6px',
									right: '-6px',
									backgroundColor: 'white',
									borderRadius: '50%',
									padding: '0',
									border: 'none',
									cursor: 'pointer',
								}}
								onClick={() => handleRemove(idx)}
							>
								<img src="/images/ImageRemove.svg" alt="" />
							</button>
						</Card>
					))}
				</Box>
			</Box>
		)
	}

	return (
		<Box>
			{showLabel && label && (
				<Typography 
					variant="body2" 
					sx={{ 
						mb: 1, 
						fontSize: '14px', 
						fontWeight: '500', 
						color: '#313131' ,
						...props.labelStyle
					}}
				>
					{label}
				</Typography>
			)}
			{preview ? (
				<Box sx={{ position: 'relative', display: 'inline-block' }}>
					<Card
						variant="outlined"
						sx={{
							width: width,
							height: height,
							borderRadius: '12px',
							borderColor: '#DFE4EB',
							position: 'relative',
							overflow: 'unset',
						}}
					>
						<CardMedia
							sx={{
								height: '100%',
								borderRadius: '12px',
							}}
							image={preview}
						/>
						<button
							style={{
								position: 'absolute',
								top: '-6px',
								right: '-6px',
								backgroundColor: 'white',
								borderRadius: '50%',
								padding: '0',
								border: 'none',
								cursor: 'pointer',
							}}
							onClick={() => handleRemove()}
						>
							<img src="/images/ImageRemove.svg" alt="" />
						</button>
					</Card>
				</Box>
			) : (
				<Button
					sx={{
						width: width,
						height: height,
						borderRadius: '8px',
						aspectRatio: '1 / 1',
						backgroundColor: '#F9F9F9',
						textTransform: 'none',
						padding: '0',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 1,
						border: '1px dashed #E0E0E0',
						position: 'relative',
						overflow: 'hidden',
						'&:hover': {
							backgroundColor: '#F5F5F5',
							borderColor: '#7CA6BD',
						},
						'& input[type="file"]': {
							position: 'absolute',
							inset: 0,
							opacity: 0,
							width: '100%',
							height: '100%',
							cursor: 'pointer',
						},
					}}
					component="label"
					variant="outlined"
				>
					<img
						src="/images/DragDrop.svg"
						alt="upload"
						style={{ width: '24px', height: '24px' }}
					/>
					<Typography
						variant="caption"
						sx={{
							color: '#666666',
							textAlign: 'center',
							fontSize: '12px',
						}}
					>
						Click to upload
					</Typography>
					<VisuallyHiddenInput
						type="file"
						accept={accept}
						onChange={handleChange}
						{...props}
					/>
				</Button>
			)}
		</Box>
	)
}

export default memo(ImageUploader)

